import React, { useState, useEffect } from 'react';
import { useNav, useUser } from '../App';
import { Box, TextField, Button, Typography, CircularProgress } from '@mui/material';
import { authAPI } from '../services/api';
import api from '../services/api';

function Login() {
  const navigate = useNav();
  const { setUser } = useUser();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(null);

  useEffect(() => {
    if (step !== 'otp') return;
    setSecondsLeft(300);
    const interval = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) { clearInterval(interval); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [step]);

  const handleSendOTP = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authAPI.sendOTP(phone);
      setStep('otp');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP');
    }
    setLoading(false);
  };

  const handleVerifyOTP = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authAPI.verifyOTP(phone, otp);
      localStorage.setItem('binko_token', response.data.token);
      localStorage.setItem('temp_phone', phone);

      if (response.data.isNewUser) {
        // New user — go straight to onboarding, no status check needed
        navigate('/onboarding');
        return;
      }

      // Existing user — fetch effectiveStatus before routing
      try {
        const meRes = await api.get('/auth/me');
        if (meRes.data.status === 'success') {
          const userData = meRes.data.user;
          setUser(userData);
          const status = userData.effectiveStatus;
          if (status === 'WAITLISTED') {
            navigate('/waitlist');
          } else if (status === 'BLOCKED') {
            // App.js will show BlockedScreen when user state is set
            navigate('/home');
          } else {
            navigate('/home');
          }
        } else {
          navigate('/home');
        }
      } catch (_) {
        // /auth/me failed — still let them in; App.js will re-fetch
        navigate('/home');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP');
    }
    setLoading(false);
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      color: '#f0f0f5',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.30)' },
      '&:hover fieldset': { borderColor: 'rgba(251,188,21,0.4)' },
      '&.Mui-focused fieldset': { borderColor: '#FBBC15' },
    },
    '& .MuiInputLabel-root': { color: '#7a7a8c' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#FBBC15' },
    '& input': { color: '#f0f0f5' },
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0e0e14',
      padding: '24px 16px',
    }}>
      {/* Logo */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
        <img src="/logo.png" alt="binko" style={{ height: '56px', width: 'auto' }} />
      </Box>

      {/* Card */}
      <Box sx={{
        width: '100%',
        maxWidth: '380px',
        background: '#16161f',
        border: '0.5px solid rgba(255,255,255,0.07)',
        borderRadius: '20px',
        padding: '28px 24px',
      }}>
        {step === 'phone' ? (
          <Box>
            <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#f0f0f5', mb: 1 }}>
              Welcome back
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#7a7a8c', mb: 3 }}>
              Enter your phone number to continue
            </Typography>
            <TextField
              fullWidth
              label="Phone Number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+919999999999"
              margin="normal"
              disabled={loading}
              sx={inputSx}
            />
            {error && (
              <Typography sx={{ fontSize: '13px', color: '#ff4d4d', mt: 1 }}>{error}</Typography>
            )}
            <Button
              fullWidth
              variant="contained"
              onClick={handleSendOTP}
              disabled={loading || !phone}
              sx={{
                mt: 3, py: 1.5, borderRadius: '12px',
                background: '#FBBC15', color: '#0e0e14',
                fontWeight: 700, fontSize: '15px',
                '&:hover': { background: '#e6ac10' },
                '&:disabled': { background: 'rgba(251,188,21,0.3)', color: 'rgba(0,0,0,0.4)' },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: '#0e0e14' }} /> : 'Send OTP'}
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#f0f0f5', mb: 1 }}>
              Enter OTP
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#7a7a8c', mb: 1 }}>
              Sent to {phone}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: secondsLeft > 0 ? '#7a7a8c' : '#ff4d4d', mb: 3 }}>
              {secondsLeft > 0
                ? `OTP expires in ${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`
                : 'OTP expired. Please go back and request a new one.'}
            </Typography>
            <TextField
              fullWidth
              label="OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="123456"
              margin="normal"
              disabled={loading}
              sx={inputSx}
            />
            {error && (
              <Typography sx={{ fontSize: '13px', color: '#ff4d4d', mt: 1 }}>{error}</Typography>
            )}
            <Button
              fullWidth
              variant="contained"
              onClick={handleVerifyOTP}
              disabled={loading || !otp}
              sx={{
                mt: 3, py: 1.5, borderRadius: '12px',
                background: '#FBBC15', color: '#0e0e14',
                fontWeight: 700, fontSize: '15px',
                '&:hover': { background: '#e6ac10' },
                '&:disabled': { background: 'rgba(251,188,21,0.3)', color: 'rgba(0,0,0,0.4)' },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: '#0e0e14' }} /> : 'Verify OTP'}
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={() => setStep('phone')}
              disabled={loading}
              sx={{ mt: 1, color: '#7a7a8c', fontSize: '13px' }}
            >
              Change Number
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default Login;
